/**
 * [MCV-1839] Helpers for linking an SSO provider (Google, Microsoft, Apple) to a
 * signed-in user from account settings.
 *
 * `createExternalAccount` / `reauthorize` send the browser away to the provider
 * and Clerk's server sends it back to the `redirectUrl` we hand over, success or
 * failure. clerk-js does nothing with that return on the web: a failed attempt
 * is recorded only on the ExternalAccount (`verification.error`), and the
 * failure can also ride back on the URL as `__clerk_status=failed` plus
 * `__clerk_error_code` (the same convention clerk-js's own OAuth transport reads).
 * So the page we return to has to notice the failure itself, and has to be
 * the page the user left.
 */

// Added by Clerk's server to the URL it sends the browser back to.
const CLERK_RETURN_PARAMS = ['__clerk_status', '__clerk_error_code', 'rotating_token_nonce'];

const CONNECT_ATTEMPT_KEY = 'mcv:connect-external-account';

/** The URL the user is on now, without a previous attempt's Clerk params or a hash. */
export const stripClerkReturnParams = (href: string): string => {
  const url = new URL(href);
  CLERK_RETURN_PARAMS.forEach((param) => url.searchParams.delete(param));
  url.hash = '';
  return url.toString();
};

/** Where Clerk should send the browser back to: exactly the page the user is on. */
export const buildExternalAccountRedirectUrl = (href: string = window.location.href): string =>
  stripClerkReturnParams(href);

export interface ClerkReturnFailure {
  /** `__clerk_error_code`, when the server sent one. */
  code: string | null;
}

/** The failure Clerk's server reported on the URL we were sent back to, if any. */
export const readClerkReturnFailure = (
  search: string = window.location.search
): ClerkReturnFailure | null => {
  const params = new URLSearchParams(search);
  if (params.get('__clerk_status') !== 'failed') return null;
  return { code: params.get('__clerk_error_code') };
};

/** Drop Clerk's return params from the address bar, leaving the page and tab as they are. */
export const cleanClerkReturnParams = (): void => {
  const current = window.location.href;
  const cleaned = stripClerkReturnParams(current);
  // The hash is not Clerk's, so it is put back.
  const next = `${cleaned}${window.location.hash}`;
  if (next !== current) window.history.replaceState(window.history.state, '', next);
};

// sessionStorage can throw (blocked site data, private windows); the page works without it.

/** Remember which provider the user is leaving for, so the return can tell it is one. */
export const markConnectAttempt = (strategy: string): void => {
  try {
    window.sessionStorage.setItem(CONNECT_ATTEMPT_KEY, strategy);
  } catch {
    // The return is then recognised by Clerk's URL params alone.
  }
};

export const peekConnectAttempt = (): string | null => {
  try {
    return window.sessionStorage.getItem(CONNECT_ATTEMPT_KEY);
  } catch {
    return null;
  }
};

export const clearConnectAttempt = (): void => {
  try {
    window.sessionStorage.removeItem(CONNECT_ATTEMPT_KEY);
  } catch {
    // Nothing to clear.
  }
};
